import { EasmScanConfigurationView } from '../view/EasmScanConfigurationView';
import { templateEasmScanConfiguration } from '../view/template';
import { EasmScanConfigurationRepository } from '../repository/EasmScanConfigurationRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { EasmScanConfigurationWebComponent } from '..';
import { EASMScanConfigurationEvents, EASMScanConfigurationResponse } from '../types';
import { DataGridItem, GridType } from '../../easmScanConfigurationAssetGrid/types';
import { GzTabChangeEvent } from '../../../../ui/gzTabs/gzTabGroup/events/GzTabChangeEvent';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { getErrorMessage } from '../../../utils';
import { GridEvents } from '../../../../grid/types/common';
import { GzTabGroupEvents } from '../../../../ui/gzTabs/gzTabGroup/types/types';

export class EasmScanConfigurationController extends BaseWebComponentController {
  public component: EasmScanConfigurationWebComponent;
  public dataRepository: EasmScanConfigurationRepository;
  public view: EasmScanConfigurationView;
  public viewConstructor: typeof EasmScanConfigurationView;
  public dataRepositoryConstructor: typeof EasmScanConfigurationRepository;

  private sourceTabType: GridType = GridType.SCAN_LIST;
  private gridView: any;

  constructor(component: EasmScanConfigurationWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: EasmScanConfigurationView,
      viewTemplate: templateEasmScanConfiguration,
      dataRepositoryConstructor: EasmScanConfigurationRepository,
    });
  }

  public setCompanyIdOnView(companyId: string): void {
    this.view.companyId = companyId;
  }

  async saveConfiguration(startScan: boolean): Promise<void> {
    const dataFromScheduler = this.view.dom.$scheduler.controller?.getDataForUpdate();
    const include = this.gridView?.dataProvider.getScanListData();
    const exclude = this.gridView?.dataProvider.getScanExclusionData();
    let response: EASMScanConfigurationResponse;
    try {
      response = await ConsoleRequest.createRequest<EASMScanConfigurationResponse>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'EASMScanConfigurations',
        method: 'updateAndStartScanByCompanyId',
        payload: {
          input: {
            include: include,
            exclude: exclude,
          },
          runScan: startScan,
          ...dataFromScheduler,
        },
      });
    } catch (error: any) {
      if (error?.err.exceptionType === 'Exceptions\\CannotFindIntegrationSettings') {
        this.showErrorToast(this.dependencies.translateFn(
          'EASM integration is not configured. Please contact your administrator.'
        ));
        return;
      }
      this.showErrorToast(this.dependencies.translateFn('Failed to save configuration.'));
      return;
    }

    if (!response.success) {
      this.showErrorToast(getErrorMessage(response.reason));
      return;
    }

    this.showSuccessToast('Scan configuration saved successfully');
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$assetForm,
        event: EASMScanConfigurationEvents.ADD_ASSET_TO_SCAN,
        callback: this.addAssetToScan,
      },
      {
        element: this.view.dom.$assetGrid,
        event: EASMScanConfigurationEvents.REMOVE_ASSET_FROM_SCAN,
        callback: this.removeAssetFromGrid,
      },
      {
        element: this.view.dom.$scanTabGroup,
        event: GzTabGroupEvents.TAB_CHANGE,
        callback: this.onTabChange.bind(this),
      },
      {
        element: this.view.dom.$assetGrid,
        event: GridEvents.GRID_LOADED,
        callback: this.loadGridPanel,
      },
    ];
  }

  private showSuccessToast(message: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(message: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.ERROR,
    });
  }

  public async getDataForView(): Promise<any> {
    return this.dataRepository.getDataForView();
  }

  private async addAssetToScan(event: WebComponentEvent): Promise<void> {
    const existingData: Array<DataGridItem> = this.gridView?.dataProvider.getData();
    const dataFromOtherTab: Array<DataGridItem> = this.gridView?.dataProvider.getDataFromOtherTab();

    if (existingData.some(item => item.type === event.detail.assetType && item.value.toLowerCase() === event.detail.assetName.toLowerCase())) {
      this.view.dom.$assetForm.inputError.Error = this.dependencies.translateFn(
        'The asset provided already exists in the list.'
      );

      return;
    }

    if (dataFromOtherTab.some(item => item.type === event.detail.assetType && item.value.toLowerCase() === event.detail.assetName.toLowerCase())) {
      this.view.dom.$assetForm.inputError.Error = this.dependencies.translateFn(
        'The asset provided already exists in another scan list.'
      );

      return;
    }

    let response: EASMScanConfigurationResponse;

    try {
      // validate asset using EASM API
      response = await ConsoleRequest.createRequest<EASMScanConfigurationResponse>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'EASMScanConfigurations',
        method: 'validateAsset',
        payload: {
          type: event.detail.assetType,
          value: event.detail.assetName,
          companyId: this.component.companyId,
        },
      });
    } catch (error: any) {
      if (error?.err.exceptionType === 'Exceptions\\CannotFindIntegrationSettings') {
        this.view.dom.$assetForm.inputError.Error = this.dependencies.translateFn(
          'EASM integration is not configured. Please contact your administrator.'
        );

      }
      return;
    }

    this.view.dom.$assetForm.inputError.Error = null;

    if (!response.success) {
      this.view.dom.$assetForm.inputError.Error = getErrorMessage(response.reason);
      return;
    }

    await this.gridView?.dataProvider.addAssetToScan(
      event.detail.assetName,
      event.detail.assetType,
      this.sourceTabType
    );
    await this.gridView?.controller.handleGridItemsLoad();

    this.view.showGrid();
  }

  private async removeAssetFromGrid(event: WebComponentEvent): Promise<void> {
    this.gridView?.dataProvider.deleteItem(event.detail.assetId, this.sourceTabType);

    await this.gridView?.controller.handleGridItemsLoad();

    // if the last element is deleted, show no results panel
    if (event.detail.data.length === 1) {
      this.view.showNoResultsPanel();
    }
  }

  private async onTabChange(event: GzTabChangeEvent): Promise<void> {
    this.sourceTabType = event.currentTab;

    if (this.view.dom.$assetForm.scanListForm && this.view.dom.$assetForm.scanExclusionsForm) {
      if (event.currentTab === GridType.SCAN_EXCLUSIONS) {
        this.view.dom.$assetForm.scanListForm.hidden = true;
        this.view.dom.$assetForm.scanExclusionsForm.hidden = false;
      } else if (event.currentTab === GridType.SCAN_LIST) {
        this.view.dom.$assetForm.scanExclusionsForm.hidden = true;
        this.view.dom.$assetForm.scanListForm.hidden = false;
      }
    }

    this.gridView?.dataProvider.setGridType(event.currentTab);
    await this.gridView?.controller.handleGridItemsLoad();
    await this.gridView?.controller.resetView();
    this.view.dom.$assetForm.inputError.Error = null;

    await this.loadGridPanel();
  }

  private async loadGridPanel(): Promise<void> {
    if (this.gridView?.dataProvider.getData().length === 0) {
      this.view.showNoResultsPanel();
    } else {
      this.view.showGrid();
    }
  }

  public async render(): Promise<void> {
    await super.render();

    this.gridView = this.view.dom.$assetGrid.controller?.view.dom.$gridView;
  }
}
